import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaFileUpload } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";
import { MdClose } from "react-icons/md";
import { RiEditFill } from "react-icons/ri";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// Full country name (as stored on the seller profile, from the same country-list
// package the backend's /user/countries endpoint uses) -> ISO code, limited to the
// countries /seller/update-bank-details actually accepts.
const BANK_SUPPORTED_COUNTRY_CODES = {
  "United States of America": "US",
  "United Kingdom of Great Britain and Northern Ireland": "GB",
  "Canada": "CA",
  "Australia": "AU",
  "Germany": "DE",
  "France": "FR",
  "Italy": "IT",
  "Spain": "ES",
  "Netherlands": "NL",
  "Belgium": "BE",
  "Austria": "AT",
  "Switzerland": "CH",
  "Sweden": "SE",
  "Norway": "NO",
  "Denmark": "DK",
  "Finland": "FI",
  "Ireland": "IE",
  "Portugal": "PT",
  "Luxembourg": "LU",
  "Malta": "MT",
  "Cyprus": "CY",
  "Estonia": "EE",
  "Latvia": "LV",
  "Lithuania": "LT",
  "Slovenia": "SI",
  "Slovakia": "SK",
  "Czechia": "CZ",
  "Hungary": "HU",
  "Poland": "PL",
  "Romania": "RO",
  "Bulgaria": "BG",
  "Croatia": "HR",
  "Greece": "GR",
};

// The saved country may be upper-cased ("UNITED STATES OF AMERICA", from signup) or a
// short form ("United States"), so match ignoring case and accept common short names.
const BANK_COUNTRY_ALIASES = {
  "united states": "US",
  usa: "US",
  "united kingdom": "GB",
  uk: "GB",
};
const getBankCountryCode = (countryName) => {
  const name = String(countryName || "").trim().toLowerCase();
  if (!name) return null;
  const match = Object.entries(BANK_SUPPORTED_COUNTRY_CODES).find(
    ([supported]) => supported.toLowerCase() === name
  );
  return match ? match[1] : BANK_COUNTRY_ALIASES[name] || null;
};

const BankDetailsEdit = ({ profiledate, editicon, sellerId }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  const [save, setSave] = useState(false);
  const [getbankdetals, setGetbankdetals] = useState();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const passbookFile = watch("passbook")?.[0];

  // Pull country/currency/account-holder-type from the already-saved Seller Info
  // instead of asking for them again in this form.
  const bankCountryCode = getBankCountryCode(profiledate?.country);
  const bankCurrency = (profiledate?.currency || "usd").toLowerCase();
  const bankAccountHolderType = profiledate?.seller_type === "business" ? "company" : "individual";

  const uploadPassbookFile = async (file) => {
    const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
    const presignResponse = await axios.post(
      `${API_BASE_URL}/aws/generate-presigned-url`,
      { files: [{ fileName: file.name, fileType: file.type }] },
      { headers: { token, "Content-Type": "application/json" } }
    );
    const [{ presignedUrl, fileUrl }] = presignResponse.data.directory;
    await axios.put(presignedUrl, file, { headers: { "Content-Type": file.type } });
    return fileUrl;
  };

  const onSubmit = async (data) => {
    setSubmitError("");

    if (!bankCountryCode) {
      setSubmitError(
        `Bank account details aren't supported yet for ${profiledate?.country || "this country"}.`
      );
      return;
    }

    setSubmitting(true);
    try {
      const uploadedFile = data.passbook?.[0];
      let passbookUrl;
      if (uploadedFile) {
        passbookUrl = await uploadPassbookFile(uploadedFile);
      }

      const body = {
        country: bankCountryCode,
        currency: bankCurrency,
        account_number: data.accountNumber,
        account_holder_name: data.beneficiaryName,
        account_holder_type: bankAccountHolderType,
      };
      // routing_number must be digits-only; IFSC/SWIFT codes are alphanumeric, so
      // only send it where the backend actually validates/requires it (US).
      if (bankCountryCode === "US" && data.ifscOrSwift) {
        body.routing_number = data.ifscOrSwift;
      }
      if (passbookUrl) {
        body.passbook_cancelled_cheque = passbookUrl;
      }

      const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
      const response = await axios.post(
        `${API_BASE_URL}/seller/update-bank-details`,
        body,
        { headers: { token, "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        await fetchBankDetails();
        setSave(false);
      } else {
        setSubmitError(response.data.message || "Failed to save bank details.");
      }
    } catch (error) {
      setSubmitError(
        error.response?.data?.message || error.message || "Failed to save bank details."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const fetchBankDetails = async () => {
    // Nothing saved yet (or profile not loaded) - leave the form empty
    if (!profiledate?.stripe_account_id || !profiledate?.stripe_bank_id) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/seller/get-bank-details`,
        {
          stripeBankId: profiledate.stripe_bank_id,
          stripeAccountId: profiledate.stripe_account_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            token:
              sessionStorage.getItem("accessToken") ||
              localStorage.getItem("accessToken"),
          },
        }
      );

      // console.log("Bank details:", res.data);
      setGetbankdetals(res.data.result);
    } catch (error) {
      console.error(
        "Error fetching bank details:",
        error.response?.data || error.message
      );
    }
  };

  // Re-run once the seller profile arrives; on first render it is usually still loading
  useEffect(() => {
    fetchBankDetails();
  }, [profiledate?.stripe_account_id, profiledate?.stripe_bank_id]);

  // Preload existing bank details into the form whenever edit mode opens
  useEffect(() => {
    if (save && getbankdetals) {
      setValue("bankName", getbankdetals.bank_name || "");
      setValue("beneficiaryName", getbankdetals.account_holder_name || "");
      setValue("accountNumber", getbankdetals.last4 || "");
      setValue("ifscOrSwift", getbankdetals.routing_number || "");
    }
  }, [save, getbankdetals, setValue]);

  return (
    <div>
      <div className="p-0  sm:p-5 ">
        <div className="flex flex-row justify-between border-b-2">
          <p className="text-lg text-[#095228] font-medium">
            Bank Account Details
          </p>
          {!sellerId &&
            (save === true ? (
              <button
                onClick={() => setSave(false)}
                className="text-xs m-1 bg-green-800 py-1 rounded-xl px-3 font-medium text-white file:"
              >
                Close
              </button>
            ) : (
              <button onClick={() => setSave(true)} className="text-xs">
                <RiEditFill className="text-[18px]" />
              </button>
            ))}
        </div>
        {save === true ? (
          <div className="mt-6">
            {!bankCountryCode && (
              <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 mb-4">
                Bank account details aren't supported yet for {profiledate?.country || "this country"}.
              </p>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Bank Name
                </label>
                <input
                  type="text"
                  {...register("bankName", { required: true })}
                  placeholder="Enter Your Bank Name"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
                {errors.bankName && (
                  <p className="text-sm text-red-500">Bank Name is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Beneficiary Name
                </label>
                <input
                  type="text"
                  {...register("beneficiaryName", { required: true })}
                  placeholder="Enter Your Beneficiary Name"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
                {errors.beneficiaryName && (
                  <p className="text-sm text-red-500">
                    Beneficiary Name is required
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  {...register("accountNumber", { required: true })}
                  placeholder="Enter Your Account Number"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
                {errors.accountNumber && (
                  <p className="text-sm text-red-500">
                    Account Number is required
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  SWIFT Code
                </label>
                <input
                  type="text"
                  {...register("ifscOrSwift", { required: true })}
                  placeholder="Enter Your SWIFT Code"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
                {errors.ifscOrSwift && (
                  <p className="text-sm text-red-500">
                    SWIFT Code is required
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Passbook/Cancelled Cheque
                </label>

                <div className="flex items-center gap-3">
                  <div className="flex items-center w-full border rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-green-600">
                    <input
                      type="file"
                      accept="application/pdf"
                      id="passbook-upload"
                      {...register("passbook", { required: true })}
                      className="hidden"
                    />

                    <label
                      htmlFor="passbook-upload"
                      className="flex-grow cursor-pointer text-sm text-gray-500 truncate"
                    >
                      {passbookFile
                        ? passbookFile.name
                        : "Upload Passbook or Cheque"}
                    </label>

                    <label htmlFor="passbook-upload" className="cursor-pointer">
                      <FaFileUpload className="text-[25px]" />
                    </label>
                  </div>

                  {passbookFile && (
                    <button
                      type="button"
                      onClick={() => setValue("passbook", null)}
                      className="text-red-600 hover:text-red-800 text-lg"
                      title="Remove file"
                    >
                      <MdClose />
                    </button>
                  )}
                </div>

                {errors.passbook && (
                  <p className="text-sm text-red-500">
                    Passbook or Cancelled Cheque is required
                  </p>
                )}
              </div>

              {submitError && (
                <p className="text-sm text-red-500">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-4 px-4 py-2 bg-green-700 text-white rounded-full hover:bg-green-800 disabled:opacity-60"
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </form>
          </div>
        ) : (
          <div className="space-y-5 mt-5">
            <div>
              <p className="text-gray-500 font-medium text-[14px]">
                Beneficiary Name
              </p>
              <p className="text-[14px] font-medium">
                {getbankdetals?.account_holder_name || "xxxxxxx"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium text-[14px]">Bank Name</p>
              <p className="text-[14px] font-medium">
                {" "}
                {getbankdetals?.bank_name || "xxxxxxx"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium text-[14px]">
                Account Number
              </p>
              <p className="text-[14px] font-medium">
                {" "}
                {getbankdetals?.last4 || "xxxxxxx"}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium text-[14px]">
                SWIFT Code
              </p>
              <p className="text-[14px] font-medium">
                {getbankdetals?.routing_number || "xxxxxxx"}
              </p>
            </div>
            <div className=" mt-5">
              <span className="text-gray-500 font-medium">
                Passbook/Cancelled Cheque
              </span>
              <div className="flex items-center gap-2 mt-1">
                <a
                  href={`/image/backgroundprofile.png`}
                  className="text-blue-600 underline text-sm"
                >
                  {"xxxxxxx"}
                </a>
                <button className="text-red-500 text-lg">
                  <IoMdCloseCircle />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankDetailsEdit;
